// src/pages/Attendance.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SettingsIcon from "@mui/icons-material/Settings";
import NoteAltIcon from "@mui/icons-material/NoteAlt";

import { db } from "../firebase";
import { ref, get, set, update } from "firebase/database";

/* ------------------- MONTH CONFIG ------------------- */

const MONTHS = [
  { key: "JAN", label: "Jan", monthNumber: 1 },
  { key: "FEB", label: "Feb", monthNumber: 2 },
  { key: "MAR", label: "Mar", monthNumber: 3 },
  { key: "APR", label: "Apr", monthNumber: 4 },
  { key: "MAY", label: "May", monthNumber: 5 },
  { key: "JUN", label: "Jun", monthNumber: 6 },
  { key: "JUL", label: "Jul", monthNumber: 7 },
  { key: "AUG", label: "Aug", monthNumber: 8 },
  { key: "SEP", label: "Sep", monthNumber: 9 },
  { key: "OCT", label: "Oct", monthNumber: 10 },
  { key: "NOV", label: "Nov", monthNumber: 11 },
  { key: "DEC", label: "Dec", monthNumber: 12 }
];

/* ---------- Column widths + sticky offsets ---------- */

const COL_WIDTHS = {
  sr: 64,
  employee: 220,
  CL: 64,
  SL: 64,
  EH: 64,
  TL: 64
};

const STICKY_LEFT = {
  sr: 0,
  employee: COL_WIDTHS.sr
};

// stick the header rows at the top of the scroll container
const HEADER_TOP = 0;
const SUBHEADER_TOP = 40; // ~ first header row height

/* ------------------- EMPTY STRUCTURES ------------------- */

function emptyMonth(uid, year, monthKey, monthNumber) {
  return {
    uid,
    year,
    month: monthKey,
    monthNumber,
    late: 0,
    holiday: 0,
    total: 0,
    note: "",
    updatedAt: null,
    updatedBy: null
  };
}

function emptyYearSummary(uid, year) {
  return {
    uid,
    year,
    CL: 0,
    SL: 0,
    EH: 0,
    TL: 0,
    ehNote: "",
    updatedAt: null,
    updatedBy: null
  };
}

/* ============================================================================================
   RULES DIALOG
================================================================================================ */
function RulesDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    lateUntil: "11:30 AM",
    halfDayFrom: "11:30 AM",
    halfDayTo: "2:00 PM",
    fullDayAfter: "4:00 PM",
    bufferTime: "10:10 AM",
    exceptions: "",
    notes: ""
  });

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      try {
        const snap = await get(ref(db, "attendanceRules/default"));
        if (snap.exists()) {
          setForm((prev) => ({ ...prev, ...snap.val() }));
        }
      } catch (e) {
        console.error("Error loading rules:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await set(ref(db, "attendanceRules/default"), form);
      onClose();
    } catch (e) {
      console.error("Error saving rules:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attendance Rules</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Late until"
              size="small"
              value={form.lateUntil}
              onChange={(e) =>
                setForm((p) => ({ ...p, lateUntil: e.target.value }))
              }
            />
            <Stack spacing={2} direction="row">
              <TextField
                label="Half-day from"
                size="small"
                value={form.halfDayFrom}
                onChange={(e) =>
                  setForm((p) => ({ ...p, halfDayFrom: e.target.value }))
                }
              />
              <TextField
                label="Half-day to"
                size="small"
                value={form.halfDayTo}
                onChange={(e) =>
                  setForm((p) => ({ ...p, halfDayTo: e.target.value }))
                }
              />
            </Stack>
            <TextField
              label="Full-day after"
              size="small"
              value={form.fullDayAfter}
              onChange={(e) =>
                setForm((p) => ({ ...p, fullDayAfter: e.target.value }))
              }
            />
            <TextField
              label="Buffer time"
              size="small"
              value={form.bufferTime}
              onChange={(e) =>
                setForm((p) => ({ ...p, bufferTime: e.target.value }))
              }
            />
            <TextField
              label="Exceptions"
              size="small"
              multiline
              minRows={2}
              value={form.exceptions}
              onChange={(e) =>
                setForm((p) => ({ ...p, exceptions: e.target.value }))
              }
            />
            <TextField
              label="Notes"
              size="small"
              multiline
              minRows={2}
              value={form.notes}
              onChange={(e) =>
                setForm((p) => ({ ...p, notes: e.target.value }))
              }
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button disabled={saving} variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ============================================================================================
   MONTH EDIT DIALOG
================================================================================================ */

function MonthEditDialog({
  open,
  onClose,
  employee,
  monthData,
  monthKey,
  year,
  onSave
}) {
  if (!employee || !monthData) return null;

  const [local, setLocal] = useState(monthData);
  const noteRef = useRef(null);

  useEffect(() => setLocal(monthData), [monthData]);

  const updateNum = (field, val) => {
    const n = Number(val) || 0;
    setLocal((p) => ({
      ...p,
      [field]: n,
      total: field === "late" ? n + p.holiday : p.late + n
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit – {employee.name} ({monthKey} {year})
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              label="Late"
              type="number"
              value={local.late}
              onChange={(e) => updateNum("late", e.target.value)}
            />
            <TextField
              size="small"
              label="Holiday"
              type="number"
              value={local.holiday}
              onChange={(e) => updateNum("holiday", e.target.value)}
            />
          </Stack>

          <Chip color="primary" label={"Total: " + local.total} />

          <TextField
            multiline
            minRows={3}
            size="small"
            label="Comment"
            value={local.note || ""}
            inputRef={noteRef}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                const el = noteRef.current;
                if (!el) return;
                const insert = ",\n";
                const val = local.note || "";
                const start = el.selectionStart ?? val.length;
                const end = el.selectionEnd ?? val.length;
                const next = val.slice(0, start) + insert + val.slice(end);
                setLocal((p) => ({ ...p, note: next }));
                requestAnimationFrame(() => {
                  try {
                    el.selectionStart = el.selectionEnd = start + insert.length;
                  } catch {}
                });
              }
            }}
            onChange={(e) =>
              setLocal((p) => ({ ...p, note: e.target.value }))
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(local)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ============================================================================================
   YEAR SUMMARY DIALOG
================================================================================================ */

function YearSummaryDialog({
  open,
  onClose,
  employee,
  year,
  yearSummary,
  onSave
}) {
  if (!employee || !yearSummary) return null;

  const [local, setLocal] = useState(yearSummary);

  useEffect(() => setLocal(yearSummary), [yearSummary]);

  const updateNum = (field, val) => {
    const n = Number(val) || 0;
    setLocal((p) => {
      const x = { ...p, [field]: n };
      return { ...x, TL: (x.CL || 0) + (x.SL || 0) + (x.EH || 0) };
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Yearly Leave – {employee.name} ({year})
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              label="CL"
              type="number"
              value={local.CL}
              onChange={(e) => updateNum("CL", e.target.value)}
            />
            <TextField
              size="small"
              label="SL"
              type="number"
              value={local.SL}
              onChange={(e) => updateNum("SL", e.target.value)}
            />
          </Stack>

          <TextField
            size="small"
            label="EH"
            type="number"
            value={local.EH}
            onChange={(e) => updateNum("EH", e.target.value)}
          />

          <Chip color="primary" label={"Total Leave: " + local.TL} />

          <TextField
            multiline
            minRows={3}
            size="small"
            label="EH Note"
            value={local.ehNote || ""}
            onChange={(e) =>
              setLocal((p) => ({ ...p, ehNote: e.target.value }))
            }
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(local)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ============================================================================================
   MISSING EMPLOYEES DIALOG
================================================================================================ */

function MissingEmployeesDialog({
  open,
  onClose,
  year,
  employees,
  onConfirm,
  saving
}) {
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (open) {
      setSelected(employees.map((e) => e.uid)); // pre-select all
    }
  }, [open, employees]);

  const toggle = (uid) => {
    setSelected((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleConfirm = () => {
    onConfirm(selected);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add missing employees to {year}</DialogTitle>
      <DialogContent dividers>
        {employees.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            All active employees already have a table for {year}.
          </Typography>
        ) : (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              These active employees don’t have a leave/attendance table for{" "}
              <b>{year}</b>. Select the ones you want to add.
            </Typography>
            {employees.map((e) => (
              <FormControlLabel
                key={e.uid}
                control={
                  <Checkbox
                    size="small"
                    checked={selected.includes(e.uid)}
                    onChange={() => toggle(e.uid)}
                  />
                }
                label={e.name}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || selected.length === 0 || employees.length === 0}
          onClick={handleConfirm}
        >
          Add to table
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ============================================================================================
   UTILS
================================================================================================ */

function computeYearSummary(emp) {
  let taken = 0;
  MONTHS.forEach((m) => {
    const md = emp.months[m.key];
    if (md) taken += (md.late || 0) + (md.holiday || 0);
  });

  const quotaTL = emp.yearSummary?.TL || 0;

  return {
    totalLeaveTaken: taken,
    balanceLeave: quotaTL - taken,
    quotaTL
  };
}

/* ============================================================================================
   MAIN COMPONENT
================================================================================================ */

export default function Attendance() {
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [yearOptions, setYearOptions] = useState([currentYear]); // real years only
  const [yearHasData, setYearHasData] = useState({}); // {2025: true/false}

  const [employees, setEmployees] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");

  const [rulesOpen, setRulesOpen] = useState(false);
  const [editState, setEditState] = useState({ open: false });
  const [yearEdit, setYearEdit] = useState({ open: false });

  const [activeCell, setActiveCell] = useState({
    uid: null,
    kind: null,
    field: null
  });

  const [missingDialogOpen, setMissingDialogOpen] = useState(false);

  /* ---------------- YEARS LIST (only real years, no fake future) ---------------- */

  useEffect(() => {
    const loadYears = async () => {
      try {
        const [yearSnap, monthlySnap] = await Promise.all([
          get(ref(db, "attendanceYear")),
          get(ref(db, "attendanceMonthly"))
        ]);

        const yearsSet = new Set();

        if (yearSnap.exists()) {
          Object.keys(yearSnap.val() || {}).forEach((y) => yearsSet.add(y));
        }
        if (monthlySnap.exists()) {
          Object.keys(monthlySnap.val() || {}).forEach((y) => yearsSet.add(y));
        }

        const hasDataMap = {};
        yearsSet.forEach((y) => {
          const num = Number(y);
          if (!Number.isNaN(num) && num <= currentYear) {
            hasDataMap[num] = true;
          }
        });

        const optionsSet = new Set(
          Object.keys(hasDataMap).map((y) => Number(y))
        );
        optionsSet.add(currentYear);

        const options = Array.from(optionsSet)
          .filter((n) => !Number.isNaN(n) && n <= currentYear)
          .sort((a, b) => b - a);

        setYearOptions(options.length ? options : [currentYear]);
        setYearHasData(hasDataMap);

        setYear((prev) => {
          if (prev && options.includes(prev)) return prev;
          return options[0] ?? currentYear;
        });
      } catch (e) {
        console.error("Error loading year options:", e);
        setYearOptions([currentYear]);
        setYearHasData({});
        setYear(currentYear);
      }
    };

    loadYears();
  }, [currentYear]);

  const filteredEmployees = useMemo(() => {
    const t = search.toLowerCase();
    if (!t) return employees;
    return employees.filter((e) =>
      (e.name || "").toLowerCase().includes(t)
    );
  }, [employees, search]);

  const selectedEmp =
    employees.find((e) => e.uid === editState.uid) || null;

  const selectedMonthData =
    selectedEmp?.months?.[editState.monthKey] ||
    (selectedEmp
      ? emptyMonth(
          selectedEmp.uid,
          year,
          editState.monthKey,
          MONTHS.find((m) => m.key === editState.monthKey)?.monthNumber || 1
        )
      : null);

  const selectedYearEmp =
    employees.find((e) => e.uid === yearEdit.uid) || null;

  const selectedYearSummary =
    selectedYearEmp?.yearSummary ||
    (selectedYearEmp
      ? emptyYearSummary(selectedYearEmp.uid, year)
      : null);

  /* ---------------- LOAD DATA FOR SELECTED YEAR ---------------- */

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const empSnap = await get(ref(db, "employees"));
        const arr = [];
        if (empSnap.exists()) {
          const data = empSnap.val();
          Object.entries(data).forEach(([uid, d]) => {
            if (d.onboardingStatus === "OFFBOARDED" || d.active === false)
              return;

            arr.push({
              uid,
              name: d.fullName || "Unknown",
              months: {},
              yearSummary: null
            });
          });
        }

        const yearSnap = await get(ref(db, `attendanceYear/${year}`));
        const yearMap = yearSnap.exists() ? yearSnap.val() : {};

        const attSnap = await get(ref(db, `attendanceMonthly/${year}`));
        const att = attSnap.exists() ? attSnap.val() : {};

        arr.forEach((emp) => {
          if (att[emp.uid]) {
            const mObj = {};
            Object.entries(att[emp.uid]).forEach(([mk, md]) => {
              mObj[mk] = {
                ...md,
                total:
                  md.total ??
                  ((md.late || 0) + (md.holiday || 0))
              };
            });
            emp.months = mObj;
          }

          if (yearMap[emp.uid]) {
            emp.yearSummary = {
              ...emptyYearSummary(emp.uid, year),
              ...yearMap[emp.uid]
            };
          }
        });

        setEmployees(arr);
      } catch (e) {
        console.error("Error loading attendance:", e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [year]);

  /* ---------------- MISSING EMPLOYEES FOR THIS YEAR ---------------- */

  const missingEmployees = useMemo(
    () =>
      employees.filter((e) => {
        const hasMonths = e.months && Object.keys(e.months).length > 0;
        const hasSummary = !!e.yearSummary;
        return !(hasMonths && hasSummary);
      }),
    [employees]
  );

  /* ---------------- SAVE MONTH ---------------- */

  const handleSaveMonth = async (updated) => {
    if (!selectedEmp) return;
    const uid = selectedEmp.uid;
    const mk = editState.monthKey;
    const meta = MONTHS.find((m) => m.key === mk);

    setEmployees((prev) =>
      prev.map((e) =>
        e.uid !== uid
          ? e
          : {
              ...e,
              months: {
                ...e.months,
                [mk]: {
                  ...(e.months[mk] ||
                    emptyMonth(uid, year, mk, meta.monthNumber)),
                  ...updated,
                  total: (updated.late || 0) + (updated.holiday || 0)
                }
              }
            }
      )
    );

    setSaving(true);
    try {
      await set(
        ref(db, `attendanceMonthly/${year}/${uid}/${mk}`),
        {
          ...updated,
          uid,
          year,
          month: mk,
          monthNumber: meta.monthNumber,
          updatedAt: new Date().toISOString(),
          updatedBy: "admin"
        }
      );

      setYearHasData((prev) => ({ ...prev, [year]: true }));
    } catch (e) {
      console.error("Month save error:", e);
    } finally {
      setSaving(false);
      setEditState({ open: false });
    }
  };

  /* ---------------- SAVE YEAR SUMMARY ---------------- */

  const handleSaveYear = async (updated) => {
    if (!selectedYearEmp) return;
    const uid = selectedYearEmp.uid;

    setEmployees((prev) =>
      prev.map((e) =>
        e.uid !== uid
          ? e
          : {
              ...e,
              yearSummary: { ...updated }
            }
      )
    );

    setSaving(true);
    try {
      await set(ref(db, `attendanceYear/${year}/${uid}`), {
        ...updated,
        uid,
        year,
        updatedAt: new Date().toISOString(),
        updatedBy: "admin"
      });

      setYearHasData((prev) => ({ ...prev, [year]: true }));
    } catch (e) {
      console.error("Year save error:", e);
    } finally {
      setSaving(false);
      setYearEdit({ open: false });
    }
  };

  /* ---------------- INITIALIZE YEAR TABLE (ALL EMPLOYEES) ---------------- */

  const handleInitializeYear = async () => {
    if (!employees.length) return;
    setSaving(true);

    try {
      const updates = {};
      const now = new Date().toISOString();

      employees.forEach((emp) => {
        MONTHS.forEach((m) => {
          if (!emp.months[m.key]) {
            updates[
              `attendanceMonthly/${year}/${emp.uid}/${m.key}`
            ] = {
              ...emptyMonth(emp.uid, year, m.key, m.monthNumber),
              updatedAt: now,
              updatedBy: "admin"
            };
          }
        });

        if (!emp.yearSummary) {
          updates[`attendanceYear/${year}/${emp.uid}`] = {
            ...emptyYearSummary(emp.uid, year),
            updatedAt: now,
            updatedBy: "admin"
          };
        }
      });

      await update(ref(db), updates);

      setEmployees((prev) =>
        prev.map((emp) => {
          const months = { ...emp.months };
          MONTHS.forEach((m) => {
            if (!months[m.key]) {
              months[m.key] = emptyMonth(
                emp.uid,
                year,
                m.key,
                m.monthNumber
              );
            }
          });
          return {
            ...emp,
            months,
            yearSummary:
              emp.yearSummary || emptyYearSummary(emp.uid, year)
          };
        })
      );

      setYearHasData((prev) => ({ ...prev, [year]: true }));
    } catch (e) {
      console.error("Init year error:", e);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- ADD ONLY MISSING EMPLOYEES ---------------- */

  const handleAddMissingEmployees = async (selectedIds) => {
    if (!selectedIds.length) {
      setMissingDialogOpen(false);
      return;
    }
    setSaving(true);
    try {
      const updates = {};
      const now = new Date().toISOString();

      employees.forEach((emp) => {
        if (!selectedIds.includes(emp.uid)) return;

        MONTHS.forEach((m) => {
          if (!emp.months[m.key]) {
            updates[
              `attendanceMonthly/${year}/${emp.uid}/${m.key}`
            ] = {
              ...emptyMonth(emp.uid, year, m.key, m.monthNumber),
              updatedAt: now,
              updatedBy: "admin"
            };
          }
        });

        if (!emp.yearSummary) {
          updates[`attendanceYear/${year}/${emp.uid}`] = {
            ...emptyYearSummary(emp.uid, year),
            updatedAt: now,
            updatedBy: "admin"
          };
        }
      });

      await update(ref(db), updates);

      setEmployees((prev) =>
        prev.map((emp) => {
          if (!selectedIds.includes(emp.uid)) return emp;

          const months = { ...emp.months };
          MONTHS.forEach((m) => {
            if (!months[m.key]) {
              months[m.key] = emptyMonth(
                emp.uid,
                year,
                m.key,
                m.monthNumber
              );
            }
          });

          return {
            ...emp,
            months,
            yearSummary:
              emp.yearSummary || emptyYearSummary(emp.uid, year)
          };
        })
      );

      setYearHasData((prev) => ({ ...prev, [year]: true }));
      setMissingDialogOpen(false);
    } catch (e) {
      console.error("Add missing employees error:", e);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- RENDER ---------------- */

  const selectedYearHasData = !!yearHasData[year];
  const canCreateYearTable =
    !selectedYearHasData && year <= currentYear && !saving;

  const showAddMissingButton =
    selectedYearHasData && missingEmployees.length > 0;

  return (
    <Box>
      {/* HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <CalendarMonthIcon />
          <Typography variant="h5" fontWeight={700}>
            Attendance &amp; Leave
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search employee..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              label="Year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant={selectedYearHasData ? "outlined" : "contained"}
            size="small"
            disabled={!canCreateYearTable}
            onClick={handleInitializeYear}
          >
            {selectedYearHasData ? "Year table ready" : "Create year table"}
          </Button>

          {showAddMissingButton && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => setMissingDialogOpen(true)}
              disabled={saving}
            >
              Add {missingEmployees.length} missing
            </Button>
          )}

          <Button
            size="small"
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setRulesOpen(true)}
          >
            Rules
          </Button>

          {saving && <CircularProgress size={22} />}
        </Stack>
      </Stack>

      {/* TABLE / EMPTY STATE */}
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : !selectedYearHasData ? (
            <Box
              sx={{
                p: 4,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center"
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Attendance table for <b>{year}</b> has not been generated
                yet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click the button below to create the table for all active
                employees.
              </Typography>
              <Button
                variant="contained"
                disabled={!canCreateYearTable}
                onClick={handleInitializeYear}
              >
                Generate year table
              </Button>
              {year > currentYear && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  You can only generate tables up to the current year (
                  {currentYear}).
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                maxHeight: "calc(100vh - 180px)",
                overflowX: "auto",
                overflowY: "auto"
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: 1500,
                  borderCollapse: "separate",
                  borderSpacing: 0
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* SR */}
                    <TableCell
                      rowSpan={2}
                      sx={{
                        position: "sticky",
                        top: HEADER_TOP,
                        left: STICKY_LEFT.sr,
                        zIndex: 6,
                        bgcolor: "grey.100",
                        width: COL_WIDTHS.sr,
                        minWidth: COL_WIDTHS.sr,
                        fontWeight: 700
                      }}
                    >
                      SR
                    </TableCell>

                    {/* EMPLOYEE */}
                    <TableCell
                      rowSpan={2}
                      sx={{
                        position: "sticky",
                        top: HEADER_TOP,
                        left: STICKY_LEFT.employee,
                        zIndex: 6,
                        bgcolor: "grey.100",
                        width: COL_WIDTHS.employee,
                        minWidth: COL_WIDTHS.employee,
                        fontWeight: 700
                      }}
                    >
                      Employee
                    </TableCell>

                    {/* YEARLY */}
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        position: "sticky",
                        top: HEADER_TOP,
                        zIndex: 5,
                        bgcolor: "grey.100",
                        width: COL_WIDTHS.CL,
                        minWidth: COL_WIDTHS.CL,
                        fontWeight: 700
                      }}
                    >
                      CL
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        position: "sticky",
                        top: HEADER_TOP,
                        zIndex: 5,
                        bgcolor: "grey.100",
                        width: COL_WIDTHS.SL,
                        minWidth: COL_WIDTHS.SL,
                        fontWeight: 700
                      }}
                    >
                      SL
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        position: "sticky",
                        top: HEADER_TOP,
                        zIndex: 5,
                        bgcolor: "grey.100",
                        width: COL_WIDTHS.EH,
                        minWidth: COL_WIDTHS.EH,
                        fontWeight: 700
                      }}
                    >
                      EH
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        position: "sticky",
                        top: HEADER_TOP,
                        zIndex: 5,
                        bgcolor: "grey.100",
                        width: COL_WIDTHS.TL,
                        minWidth: COL_WIDTHS.TL,
                        fontWeight: 700
                      }}
                    >
                      TL
                    </TableCell>

                    {MONTHS.map((m) => (
                      <TableCell
                        key={m.key}
                        align="center"
                        colSpan={2}
                        sx={{
                          position: "sticky",
                          top: HEADER_TOP,
                          zIndex: 4,
                          bgcolor: "grey.100",
                          fontWeight: 700
                        }}
                      >
                        {m.label}
                      </TableCell>
                    ))}

                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        position: "sticky",
                        top: HEADER_TOP,
                        zIndex: 5,
                        bgcolor: "grey.100",
                        fontWeight: 700
                      }}
                    >
                      Total Leave Taken
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        position: "sticky",
                        top: HEADER_TOP,
                        zIndex: 5,
                        bgcolor: "grey.100",
                        fontWeight: 700
                      }}
                    >
                      Balance
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    {MONTHS.map((m) => (
                      <React.Fragment key={m.key + "-sub"}>
                        <TableCell
                          align="center"
                          sx={{
                            position: "sticky",
                            top: SUBHEADER_TOP,
                            zIndex: 4,
                            bgcolor: "grey.50",
                            fontWeight: 500,
                          }}
                        >
                          Late
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            position: "sticky",
                            top: SUBHEADER_TOP,
                            zIndex: 4,
                            bgcolor: "grey.50",
                            fontWeight: 500
                          }}
                        >
                          Holiday
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredEmployees.map((emp, idx) => {
                    const summary = computeYearSummary(emp);
                    const ys =
                      emp.yearSummary || emptyYearSummary(emp.uid, year);

                    const isNeg = summary.balanceLeave < 0;
                    const tint = isNeg ? "rgba(255,0,0,0.07)" : undefined;
                    const stickyColor = isNeg ? tint : "grey.100";

                    return (
                      <TableRow key={emp.uid} sx={{ bgcolor: tint }}>
                        {/* SR */}
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: STICKY_LEFT.sr,
                            bgcolor: stickyColor,
                            zIndex: 3,
                            width: COL_WIDTHS.sr,
                            minWidth: COL_WIDTHS.sr
                          }}
                        >
                          {idx + 1}
                        </TableCell>

                        {/* EMPLOYEE */}
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: STICKY_LEFT.employee,
                            bgcolor: stickyColor,
                            zIndex: 3,
                            width: COL_WIDTHS.employee,
                            minWidth: COL_WIDTHS.employee
                          }}
                        >
                          {emp.name}
                        </TableCell>

                        {/* YEAR FIELDS */}
                        {["CL", "SL", "EH", "TL"].map((field) => {
                          const width = COL_WIDTHS[field];
                          return (
                            <TableCell
                              key={field}
                              align="center"
                              sx={{
                                width,
                                minWidth: width,
                                cursor: "pointer",
                                bgcolor:
                                  activeCell.uid === emp.uid &&
                                  activeCell.field === field
                                    ? "action.selected"
                                    : tint,
                                "&:hover": { bgcolor: "action.hover" }
                              }}
                              onClick={() => {
                                setYearEdit({ open: true, uid: emp.uid });
                                setActiveCell({
                                  uid: emp.uid,
                                  field,
                                  kind: "year"
                                });
                              }}
                            >
                              {ys[field] || 0}
                            </TableCell>
                          );
                        })}

                        {/* MONTHS */}
                        {MONTHS.map((m) => {
                          const md =
                            emp.months[m.key] ||
                            emptyMonth(
                              emp.uid,
                              year,
                              m.key,
                              m.monthNumber
                            );

                          const note = md.note?.trim();
                          const tooltip = note
                            ? note
                            : `Late ${md.late} | Holiday ${md.holiday}`;

                          return (
                            <React.Fragment
                              key={`${emp.uid}-${m.key}`}
                            >
                              <TableCell
                                align="center"
                                sx={{
                                  cursor: "pointer",
                                  bgcolor:
                                    activeCell.uid === emp.uid &&
                                    activeCell.field ===
                                      `late_${m.key}`
                                      ? "action.selected"
                                      : undefined,
                                  "&:hover": { bgcolor: "action.hover" }
                                }}
                                onClick={() => {
                                  setEditState({
                                    open: true,
                                    uid: emp.uid,
                                    monthKey: m.key
                                  });
                                  setActiveCell({
                                    uid: emp.uid,
                                    field: `late_${m.key}`,
                                    kind: "month"
                                  });
                                }}
                              >
                                <Tooltip title={tooltip}>
                                  <span>{md.late}</span>
                                </Tooltip>
                              </TableCell>

                              <TableCell
                                align="center"
                                sx={{
                                  cursor: "pointer",
                                  bgcolor:
                                    activeCell.uid === emp.uid &&
                                    activeCell.field ===
                                      `holiday_${m.key}`
                                      ? "action.selected"
                                      : undefined,
                                  "&:hover": { bgcolor: "action.hover" }
                                }}
                                onClick={() => {
                                  setEditState({
                                    open: true,
                                    uid: emp.uid,
                                    monthKey: m.key
                                  });
                                  setActiveCell({
                                    uid: emp.uid,
                                    field: `holiday_${m.key}`,
                                    kind: "month"
                                  });
                                }}
                              >
                                <Tooltip title={tooltip}>
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      gap: 0.5,
                                      alignItems: "center"
                                    }}
                                  >
                                    <span>{md.holiday}</span>
                                    {note && (
                                      <NoteAltIcon
                                        sx={{ fontSize: 14 }}
                                      />
                                    )}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                            </React.Fragment>
                          );
                        })}

                        {/* SUMMARY */}
                        <TableCell align="center">
                          <Chip
                            size="small"
                            color="secondary"
                            label={summary.totalLeaveTaken}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            color={
                              summary.balanceLeave < 0
                                ? "error"
                                : "success"
                            }
                            label={summary.balanceLeave}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* DIALOGS */}
      <MonthEditDialog
        open={editState.open}
        onClose={() => setEditState({ open: false })}
        employee={selectedEmp}
        monthData={selectedMonthData}
        monthKey={editState.monthKey}
        year={year}
        onSave={handleSaveMonth}
      />

      <YearSummaryDialog
        open={yearEdit.open}
        onClose={() => setYearEdit({ open: false })}
        employee={selectedYearEmp}
        year={year}
        yearSummary={selectedYearSummary}
        onSave={handleSaveYear}
      />

      <RulesDialog open={rulesOpen} onClose={() => setRulesOpen(false)} />

      <MissingEmployeesDialog
        open={missingDialogOpen}
        onClose={() => setMissingDialogOpen(false)}
        year={year}
        employees={missingEmployees}
        onConfirm={handleAddMissingEmployees}
        saving={saving}
      />
    </Box>
  );
}
